import { memo, useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import { useTranslator } from '@/hooks'
import { getLocaleCountry, cookie, getStoreByLocale } from '@dy/commons/utils'
import { FOOTER_URLS, SOCIAL_LINKS } from '../layoutConfig'
import { Button, Figure, Link } from '@dy/commons/components'
import { useModal } from '@dy/commons/hooks'
import { MODALS } from '@dy/commons/context'
import styled from 'styled-components'
import { mq, vw, designGrid, getP15_1, getP15_3 } from '@dy/commons/styles'

const mediaStamp = {
  type: 'image' as const,
  alt: 'Stamp denominazione origine logo',
  default: {
    src:'/images/svg/i--stamp--denominazione-origine-protetta.svg',
    size: { width: 6, height: 8 }
  }
}
const ecoStamp = {
  type: 'image' as const,
  alt: 'Stamp evo certification',
  default: {
    src:'/images/svg/i--eco.svg',
    size: { width: 275, height: 183}
  }
}
const mozzarellaStamp = {
  type: 'image' as const,
  alt: 'Stamp Mozzarella Bufala',
  default: {
    src:'/images/svg/i--mozzarella.svg',
    size: { width: 275, height: 183}
  }
}

const arrSocialNetworks = ['instagram', 'facebook', 'linkedin', 'youtube']
const mediaSocialIcon = (name) => ({
  type: 'image' as const,
  alt: `${name} logo`,
  default: {
    src: `/images/svg/i--rrss--${name}.svg`,
    size: { width: 30, height: 30 }
  }
})

const getSocialLinks = (data, localeRegion) => data.map(name => (
  <li key={name}>
    <a href={SOCIAL_LINKS[localeRegion][name]} rel='noreferrer' target='_blank' title={name}>
      <Figure media={mediaSocialIcon(name)} />
    </a>
  </li>
))
const LOGOS = [
  {
    'url': '/images/svg/logo-50-top.svg',
    'alt': 'Logo 50 Top',
    'href': '/page/50-top-pizza '
  },
  {
    'url': '/images/svg/logo-ilif.svg',
    'alt': 'Logo I Love Italian Food'
  },
  {
    'url': '/images/svg/logo-ffm.svg',
    'alt': 'Logo 50 Top',
  },

]
const getFeaturedLogos = () => LOGOS.map((logo,idx)=> {
  return(
    <li key={idx}>
      {idx === 0 ?
        <a href={logo?.href}>
          <img src={logo?.url} alt={logo?.alt}></img>
        </a>
        :
        <img src={logo?.url} alt={logo?.alt}></img>
      }

    </li>

  )
})

export default memo(() => {
  const { t } = useTranslator()
  const { locale } = useRouter()
  const localeRegion = useMemo(() => getLocaleCountry(locale), [locale])
  const ListItemsSocialLinks = useMemo(() => getSocialLinks(arrSocialNetworks, localeRegion), [localeRegion])
  const { toggle } = useModal(MODALS.REGION_LANG)
  const FeaturedLogos =  getFeaturedLogos()
  const [location, setLocation] = useState('')

  useEffect(() => {
    const cookieCatalogRegion = cookie.get('DY_CATALOG_REGION')
    const countryCode = 'ES'

    async function getCurrentLocation() {
      const data = await import(`/public/locales/countries/${countryCode}-subdivisions.json`, { assert: { type: 'json' } })
      const dataArr = Array.from(data)
      // @ts-ignore
      const incomingLocation = dataArr.filter(item => item.value === cookieCatalogRegion.toUpperCase())?.[0]
      // @ts-ignore
      setLocation(incomingLocation ? incomingLocation.name as string : 'España')
    }

    if(location) return
    else if (getStoreByLocale[locale]) {
      const store = getStoreByLocale[locale]
      setLocation(store)
    } else if (!cookieCatalogRegion) {
      const store = getStoreByLocale[locale]
      setLocation(store ?? 'España')
      return
    } else getCurrentLocation()
  }, [location, locale])

  return (
    <>
      <RssSection>
        <div className='stamp'>
          <Figure  media={mediaStamp}/>
          <Figure  media={ecoStamp}/>
          <Figure  media={mozzarellaStamp}/>
        </div>
        <div className='row-partners'>
          <span> {t('layout.footer.partners')}</span>
          <ul>
            {FeaturedLogos}
          </ul>
        </div>
        <div className='row-copyright'>
          <span>© Mammafiore {new Date().getFullYear()}</span>
          <Link href={FOOTER_URLS[localeRegion] || '/'}>{t('layout.footer.legal_warning')}</Link>
        </div>
        <ul className='list--rrss'>
          {ListItemsSocialLinks}
        </ul>
        {locale !== 'en-EU' &&
          <DivChangeRegion>
            {location && <p>{t('modals.comercial_region.you_are_in')}{location}</p>}
            <Button onClick={toggle} className='btn--country-and-lang' outline={true} bgType='white'>{t('layout.footer.change_country_lang')}</Button>
          </DivChangeRegion>
        }
      </RssSection>
    </>
  )
})


const RssSection = styled.section`
  ${designGrid({})}
  grid-column: 1 / span 6;
  padding: 0;
  width: auto;

  ${mq.greaterThan('tablet')} {
    align-items: flex-end;
    grid-column: 1 / span 12;
    grid-row: 3 / span 1;
  }

  .stamp {
    grid-area: 2 / 1 / span 1 /span 3;
    display: flex;
    flex-direction: row;

    ${mq.greaterThan('tablet')} {
      grid-area: unset;
      grid-row: 1 / span 1;
      grid-column: 2 / span 3;
    }
    figure {
      margin-right: ${vw(10, 'mobile')};
      width: ${vw(100, 'mobile')};
      height: ${vw(75, 'mobile')};
      ${mq.greaterThan('nexus7')} {
        margin-right: ${vw(30, 'nexus7')};
        height: ${vw(100, 'nexus7')};
        width: ${vw(100, 'nexus7')};
      }
      ${mq.greaterThan('tablet')} {
        margin-right: ${vw(30, 'desktop')};

        height: ${vw(100, 'desktop')};
        width: ${vw(100, 'desktop')};
      }
      ${mq.greaterThan('desktop')} {
        margin-right: 30px;
        height: 100px;
        width: 100px;
      }
    }

    img {
      color: ${({ theme }) => theme.colors.creme};
      mix-blend-mode: multiply;
      filter:invert(49%) sepia(37%) saturate(691%) hue-rotate(313deg) brightness(74%) contrast(27%);

    }
  }

  .list--rrss {
    align-self: flex-end;
    grid-column: 4 / span 3;
    grid-row: 2 / span 1;
    display: flex;
    list-style-type: none;
    margin-bottom: ${vw(10, 'mobile')};
    height: ${vw(75, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      margin-bottom: ${vw(10, 'nexus7')};
      height: ${vw(100, 'nexus7')};

    }

    ${mq.greaterThan('tablet')} {
      height: unset;
      grid-column: 6 / span 3;
      grid-row: 2 / span 1;
      margin-bottom: 0;
    }

    li {
      align-items: center;
      display: flex;
      margin-right: ${vw(15, 'mobile')};

      ${mq.greaterThan('nexus7')} {
        margin-right: ${vw(15, 'nexus7')};
      }

      ${mq.greaterThan('tablet')} {
        margin-right: ${vw(15, 'desktop')};
      }

      ${mq.greaterThan('desktop')} {
        margin-right: 15px;
      }

      &:last-child {
        margin-right: 0;
      }
    }

    a {
      display: inline-block;
      opacity: .4;
      text-decoration: none;
      transition: 300ms opacity ease-out;

      &:hover {
        opacity: 1;
      }

      figure {
        height: ${vw(30, 'mobile')};
        width: ${vw(30, 'mobile')};

        ${mq.greaterThan('nexus7')} {
          height: ${vw(30, 'nexus7')};
          width: ${vw(30, 'nexus7')};
        }

        ${mq.greaterThan('tablet')} {
          height: ${vw(30, 'desktop')};
          min-height: 30px;
          min-width: 30px;
          width: ${vw(30, 'desktop')};
        }

        ${mq.greaterThan('desktop')} {
          height: 30px;
          width: 30px;
        }
      }
    }
  }

  .row-copyright {
    display: flex;
    flex-direction: column;
    grid-column: 1 / span 3;
    grid-row: 3 / span 1;
    margin-top: ${vw(60, 'mobile')};
    ${mq.greaterThan('nexus7')} {
      margin-top: ${vw(60, 'nexus7')};
    }

    ${mq.greaterThan('tablet')} {
      flex-direction: row;
      grid-row: 2 / span 1;
      grid-column: 2 / span 4;
      margin-top: ${vw(60, 'desktop')};
    }

    ${mq.greaterThan('desktop')} {
      margin-top: 45px;
    }

    > a,
    > span {
      ${getP15_1()}
      color: ${({ theme }) => theme.colors.white};
      margin-bottom: 0;
    }

    > span {
      margin-bottom: ${vw(5, 'mobile')};
      margin-right: 0;

      ${mq.greaterThan('nexus7')} {
        margin-bottom: ${vw(5, 'nexus7')};
      }

      ${mq.greaterThan('tablet')} {
        margin-bottom: 0;
        margin-right: ${vw(30, 'desktop')};
      }

      ${mq.greaterThan('desktop')} {
        margin-right: 30px;
      }
    }

    > a {
      transition: 300ms opacity ${({ theme }) => theme.ease};
      &:hover {
        opacity: 1;
      }
    }
  }

  .btn--country-and-lang {
    ${getP15_3()}
    border-radius: ${vw(90, 'mobile')};
    color: ${({ theme }) => theme.colors.background};
    height: ${vw(50, 'mobile')};
    font-family: ${({ theme }) => theme.fonts.secondaryFont};
    margin: auto auto 0 0;
    padding: 0 ${vw(20, 'mobile')};

    ${mq.greaterThan('nexus7')} {
      border-radius: ${vw(50, 'nexus7')};
      height: ${vw(50, 'nexus7')};
      padding: 0 ${vw(20, 'nexus7')};
    }

    ${mq.greaterThan('tablet')} {
      border-radius: ${vw(90, 'desktop')};
      height: ${vw(40, 'desktop')};
      padding: 0  ${vw(20, 'desktop')};
      letter-spacing: -0.5px;
    }

    ${mq.greaterThan('desktop')} {
      border-radius: 90px;
      height: 40px;
      padding: 0 20px;
    }

    .button__bg + div {
      ${mq.lessThan('tablet')} {
        white-space: break-spaces;
      }
    }
  }

  .row-partners {
    grid-column: 1 / span 6;
    grid-row: 1 / span 1;
    align-items: flex-end;
    margin-bottom: ${vw(25, 'mobile')};

    ${mq.greaterThan('tablet')} {
      grid-column: 6 / span 3;
      grid-row: 1 / span 1;
      margin-bottom: 0;
    }

    > ul {
      display: flex;
      flex-direction: row;
    }

    span {
      ${getP15_1()}
      color: ${({ theme }) => theme.colors.darkred};
      display: block;
      mix-blend-mode: multiply;
      filter:invert(49%) sepia(37%) saturate(691%) hue-rotate(313deg) brightness(74%) contrast(27%);
      margin: ${vw(30, 'mobile')} 0 ${vw(10, 'mobile')};

      ${mq.greaterThan('tablet')} {
        margin-bottom: ${vw(25, 'desktop')};
      }
    }

    li:first-of-type,
    li:nth-of-type(3) {
      img {
        width: ${vw(50, 'mobile')};

        ${mq.greaterThan('tablet')} {
          width: ${vw(50, 'desktop')};
        }
      }
    }

    img {
      height: ${vw(50, 'mobile')};
      color: ${({ theme }) => theme.colors.creme};
      filter:invert(49%) sepia(37%) saturate(691%) hue-rotate(313deg) brightness(74%) contrast(27%);
      margin-right : ${vw(20, 'mobile')};
      mix-blend-mode: multiply;
      width: ${vw(90, 'mobile')};

      ${mq.greaterThan('tablet')} {
        height: ${vw(50, 'desktop')};
        margin-right : ${vw(40, 'desktop')};
        width: ${vw(130, 'desktop')};
      }
    }
  }
`

const DivChangeRegion = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  grid-column: 4 / span 3;

  ${mq.greaterThan('tablet')} {
    align-items: center;
    flex-direction: row;
    grid-column: 9 / span 3;
    grid-row: 2 / span 1;
  }

  p {
    ${getP15_1()}
    color: ${({ theme }) => theme.colors.white};
    margin-bottom: 8px;

    ${mq.greaterThan('tablet')} {
      margin-bottom: 0;
      margin-right: 10px;
    }

    ${mq.greaterThan('desktop-xs')} {
      margin-right: 16px;
    }
  }
`
